// Provably Fair Slots — единая математика на тирах символов.
// Темы = скины (tier → имя символа). RTP калибруется один раз для всех тем.
import { hmacSha256Hex } from './index';
export const SLOT_WILD = 'wild';
export const SLOT_SCATTER = 'scatter';
export const SLOT_REELS = 5;
export const SLOT_ROWS = 3;
export const SLOT_FREE_SPINS = 10;
export const SLOT_MAX_FREE = 100; // защита от бесконечного ретриггера
// Бэнд одного барабана (частоты подобраны под RTP ~95%). Одинаков для всех барабанов.
const REEL_BAND = [
    ...Array(1).fill('scatter'),
    ...Array(2).fill('wild'),
    ...Array(1).fill('h1'),
    ...Array(2).fill('h2'),
    ...Array(3).fill('m1'),
    ...Array(4).fill('m2'),
    ...Array(8).fill('l1'),
    ...Array(9).fill('l2'),
];
export const SLOT_BANDS = Array.from({ length: SLOT_REELS }, () => REEL_BAND);
// Пейтейбл: выплата за 3/4/5 в линии (× ставка на линию).
export const SLOT_PAYTABLE = {
    wild: [17, 66, 280],
    h1: [14, 56, 220],
    h2: [9, 28, 110],
    m1: [5, 18, 70],
    m2: [4, 14, 45],
    l1: [4, 10, 30],
    l2: [3, 9, 22],
    scatter: [0, 0, 0],
};
// Scatter платит × общую ставку (3/4/5 разбросов в любом месте).
export const SLOT_SCATTER_PAY = [8, 30, 120];
// 10 линий выплат на сетке 5×3 (значение = ряд 0..2 для каждого барабана).
export const SLOT_PAYLINES = [
    [1, 1, 1, 1, 1],
    [0, 0, 0, 0, 0],
    [2, 2, 2, 2, 2],
    [0, 1, 2, 1, 0],
    [2, 1, 0, 1, 2],
    [1, 0, 0, 0, 1],
    [1, 2, 2, 2, 1],
    [0, 0, 1, 2, 2],
    [2, 2, 1, 0, 0],
    [1, 0, 1, 2, 1],
];
export const SLOT_LINES = SLOT_PAYLINES.length;
// ── Деривация ──
/** Позиции остановки барабанов из hash (8 hex на барабан). */
export function deriveSlotStops(serverSeed, clientSeed, nonce) {
    const hash = hmacSha256Hex(serverSeed, `${clientSeed}:${nonce}`);
    const stops = [];
    for (let i = 0; i < SLOT_REELS; i++) {
        const chunk = hash.slice(i * 8, i * 8 + 8);
        stops.push(parseInt(chunk, 16) % SLOT_BANDS[i].length);
    }
    return stops;
}
/** Видимая сетка 5×3: grid[reel][row]. */
export function buildSlotGrid(stops) {
    const grid = [];
    for (let r = 0; r < SLOT_REELS; r++) {
        const band = SLOT_BANDS[r];
        const col = [];
        for (let row = 0; row < SLOT_ROWS; row++) {
            col.push(band[(stops[r] + row) % band.length]);
        }
        grid.push(col);
    }
    return grid;
}
function evalLine(symbols) {
    let target = null;
    for (const s of symbols) {
        if (s !== SLOT_WILD && s !== SLOT_SCATTER) {
            target = s;
            break;
        }
    }
    if (target === null)
        target = SLOT_WILD; // вся линия — wild
    if (target === SLOT_SCATTER)
        return null;
    let count = 0;
    for (const s of symbols) {
        if (s === target || s === SLOT_WILD)
            count++;
        else
            break;
    }
    if (count >= 3) {
        const pay = SLOT_PAYTABLE[target][count - 3] || 0;
        if (pay > 0)
            return { tier: target, count, pay };
    }
    return null;
}
/** Оценка одного спина. lineBet — ставка на линию, totalBet — общая (для scatter). */
export function evaluateSlotSpin(grid, lineBet, totalBet) {
    const lineWins = [];
    SLOT_PAYLINES.forEach((rows, li) => {
        const symbols = rows.map((row, reel) => grid[reel][row]);
        const w = evalLine(symbols);
        if (w)
            lineWins.push({ line: li, tier: w.tier, count: w.count, win: w.pay * lineBet });
    });
    let scatterCount = 0;
    for (let r = 0; r < SLOT_REELS; r++)
        for (let row = 0; row < SLOT_ROWS; row++)
            if (grid[r][row] === SLOT_SCATTER)
                scatterCount++;
    let scatterWin = 0;
    let freeSpinsAwarded = 0;
    if (scatterCount >= 3) {
        scatterWin = SLOT_SCATTER_PAY[Math.min(scatterCount, 5) - 3] * totalBet;
        freeSpinsAwarded = SLOT_FREE_SPINS;
    }
    const totalWin = lineWins.reduce((s, w) => s + w.win, 0) + scatterWin;
    return { grid, lineWins, scatterCount, scatterWin, freeSpinsAwarded, totalWin };
}
/**
 * Полный розыгрыш: база + авто-резолв фриспинов (с ретриггером).
 * Детерминирован по сидам+nonce — тот же код на сервере и для проверки.
 * betAmount — общая ставка спина. lineBet = betAmount / SLOT_LINES.
 */
export function playSlot(serverSeed, clientSeed, nonce, betAmount) {
    const lineBet = betAmount / SLOT_LINES;
    const evalAt = (n) => {
        const stops = deriveSlotStops(serverSeed, clientSeed, n);
        return evaluateSlotSpin(buildSlotGrid(stops), lineBet, betAmount);
    };
    const base = evalAt(nonce);
    const free = [];
    let remaining = base.freeSpinsAwarded;
    let n = nonce;
    let guard = 0;
    while (remaining > 0 && guard < SLOT_MAX_FREE) {
        n += 1;
        guard += 1;
        const ev = evalAt(n);
        free.push(ev);
        remaining -= 1;
        if (ev.freeSpinsAwarded > 0)
            remaining += ev.freeSpinsAwarded;
    }
    const freeSpinsTotal = free.length;
    const totalWin = base.totalWin + free.reduce((s, e) => s + e.totalWin, 0);
    return { base, free, freeSpinsTotal, totalWin, spinsUsed: 1 + free.length };
}
export const SLOT_THEMES = [
    {
        id: 'fruits',
        name: 'Фрукты',
        symbolNames: {
            wild: 'Звезда', scatter: 'Семёрка', h1: 'Арбуз', h2: 'Виноград',
            m1: 'Слива', m2: 'Апельсин', l1: 'Лимон', l2: 'Вишня',
        },
    },
    {
        id: 'egypt',
        name: 'Египет',
        symbolNames: {
            wild: 'Фараон', scatter: 'Книга', h1: 'Око Гора', h2: 'Скарабей',
            m1: 'Анубис', m2: 'Анкх', l1: 'Пирамида', l2: 'Жук',
        },
    },
    {
        id: 'space',
        name: 'Космос',
        symbolNames: {
            wild: 'НЛО', scatter: 'Звезда', h1: 'Планета', h2: 'Ракета',
            m1: 'Спутник', m2: 'Комета', l1: 'Астероид', l2: 'Кристалл',
        },
    },
    {
        id: 'west',
        name: 'Дикий Запад',
        symbolNames: {
            wild: 'Ковбой', scatter: 'Динамит', h1: 'Шериф', h2: 'Револьвер',
            m1: 'Череп', m2: 'Подкова', l1: 'Бочонок', l2: 'Туз',
        },
    },
    {
        id: 'dogs',
        name: 'Собачки',
        symbolNames: {
            wild: 'Корги', scatter: 'Кость', h1: 'Хаски', h2: 'Шиба',
            m1: 'Мопс', m2: 'Лапа', l1: 'Будка', l2: 'Мячик',
        },
    },
];
//# sourceMappingURL=slots.js.map